import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, GitBranch, Play, CheckCircle2, Save,
  Loader2, GitMerge, AlertCircle, X, ChevronDown,
  RefreshCw, ShieldCheck, ShieldOff, ShieldQuestion, AlertTriangle, Settings, Layers, Package,
} from 'lucide-react';
import { pipelinesApi, reposApi } from '../api/client';
import EnvCheckModal from '../components/Pipeline/EnvCheckModal';
import SearchableSelect from '../components/SearchableSelect';
import { formatDistanceToNow } from 'date-fns';

// ── Single component row inside a monorepo card ──────────────────────────────
function ComponentRow({ repoName, component, globalBranch }) {
  const [branch, setBranch] = useState('');
  const [building, setBuilding] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [expanded, setExpanded] = useState(false);
  const [subsValues, setSubsValues] = useState({});
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState(null);
  const subsFetched = useRef(false);

  const effectiveBranch = branch || globalBranch;

  useEffect(() => {
    if (!expanded || subsFetched.current) return;
    subsFetched.current = true;
    setSubsLoading(true);
    setSubsError(null);
    pipelinesApi.getCloudBuildConfig(repoName, component.name)
      .then(cfg => setSubsValues(cfg.substitutions || {}))
      .catch(e => setSubsError(e.error || e.message || 'Failed to load config'))
      .finally(() => setSubsLoading(false));
  }, [expanded]); // eslint-disable-line

  function handleBuild() {
    if (!effectiveBranch) return;
    setBuilding(true);
    setResult(null);
    setError(null);
    pipelinesApi.triggerDirect({ repo: repoName, component: component.name, branch: effectiveBranch, substitutions: subsValues })
      .then(r => setResult(r))
      .catch(e => setError(e.error || e.message || 'Build failed'))
      .finally(() => setBuilding(false));
  }

  return (
    <div className="border-b border-slate-100 last:border-0">
      {/* Row header */}
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
        <button
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1.5 text-slate-400 hover:text-slate-600 transition-colors shrink-0"
          title="Show substitutions"
        >
          <Package size={13} />
          <ChevronDown size={11} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-slate-800">{component.name}</span>
          <span className="ml-2 text-[10px] font-mono text-slate-400">{component.path}</span>
          {result && (
            <Link
              to={`/builds/${result.buildId}`}
              className="ml-2 text-[10px] font-mono text-purple-400 hover:text-purple-300 underline underline-offset-2"
            >
              Build triggered → {result.buildId?.slice(0, 8)}…
            </Link>
          )}
          {error && <span className="ml-2 text-[10px] text-red-400">{error}</span>}
        </div>
        <input
          className="input text-xs py-1 w-36 shrink-0"
          placeholder={globalBranch || 'branch…'}
          value={branch}
          onChange={e => setBranch(e.target.value)}
        />
        <button
          onClick={handleBuild}
          disabled={building || !effectiveBranch}
          title={!effectiveBranch ? 'Enter a branch to enable' : `Build ${component.name}`}
          className="btn-ghost py-1 px-2.5 text-xs disabled:opacity-40 shrink-0"
        >
          {building ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
          Build
        </button>
      </div>

      {/* Substitutions panel */}
      {expanded && (
        <div className="px-10 pb-3 pt-1 bg-slate-50/70 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              Cloud Build Substitutions
              {subsLoading && <span className="ml-2 normal-case font-normal text-slate-400">Loading…</span>}
            </span>
            <button
              type="button"
              onClick={() => {
                subsFetched.current = false;
                setSubsError(null);
                setSubsLoading(true);
                pipelinesApi.getCloudBuildConfig(repoName, component.name)
                  .then(cfg => { setSubsValues(cfg.substitutions || {}); subsFetched.current = true; })
                  .catch(e => setSubsError(e.error || e.message || 'Failed to load config'))
                  .finally(() => setSubsLoading(false));
              }}
              disabled={subsLoading}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-40"
            >
              <RefreshCw size={10} className={subsLoading ? 'animate-spin' : ''} /> Reload
            </button>
          </div>

          {subsError && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              <AlertCircle size={12} className="text-red-400 shrink-0 mt-0.5" />
              <span className="text-xs text-red-400">{subsError}</span>
            </div>
          )}

          {Object.keys(subsValues).length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(subsValues).map(([key, val]) => (
                <div key={key}>
                  <label className="block text-[10px] font-mono text-blue-700 mb-0.5">{key}</label>
                  <input
                    className="input text-xs font-mono py-1"
                    value={val}
                    placeholder="(empty)"
                    onChange={e => setSubsValues(s => ({ ...s, [key]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          )}

          {!subsLoading && !subsError && Object.keys(subsValues).length === 0 && (
            <p className="text-xs text-slate-500">No substitutions found in trigger config.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Live GCP trigger row ──────────────────────────────────────────────────────


// Badge summarizing a trigger's env-checkup configuration state.
function EnvConfigBadge({ status }) {
  const map = {
    configured: { cls: 'text-emerald-600 bg-emerald-50 border-emerald-100', Icon: ShieldCheck, label: 'secret set' },
    disabled: { cls: 'text-slate-500 bg-slate-50 border-slate-200', Icon: ShieldOff, label: 'skip env check' },
    unconfigured: { cls: 'text-amber-700 bg-amber-50/70 border-amber-200/70', Icon: ShieldQuestion, label: 'needs setup' },
  };
  const s = map[status] || map.unconfigured;
  return (
    <span className={`ml-2 inline-flex items-center gap-1 text-[10px] font-medium border rounded px-1.5 py-0.5 ${s.cls}`}>
      <s.Icon size={10} /> {s.label}
    </span>
  );
}

function GcpTriggerRow({ trigger, globalBranch }) {
  const [branch, setBranch] = useState('');
  const [building, setBuilding] = useState(false);
  const [result, setResult] = useState(null);
  const [buildError, setBuildError] = useState(null);

  const [configOpen, setConfigOpen] = useState(false);
  const [subsValues, setSubsValues] = useState({});
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState(null);
  const subsFetched = useRef(false);

  // Env check state
  const [envChecking, setEnvChecking] = useState(false);
  const [envResult, setEnvResult] = useState(null);
  const [envError, setEnvError] = useState(null);
  const [envKeysModalOpen, setEnvKeysModalOpen] = useState(false);

  // Env-secret configuration (which Secret Manager secret backs this trigger)
  const [envConfig, setEnvConfigState] = useState(trigger.envCheck || { status: 'unconfigured', secretName: null });
  const [secretOptions, setSecretOptions] = useState([]);
  const [secretsLoading, setSecretsLoading] = useState(false);
  const [secretsError, setSecretsError] = useState(null);
  const [selectedSecret, setSelectedSecret] = useState('');
  const [configSaving, setConfigSaving] = useState(false);
  const [configSaveError, setConfigSaveError] = useState(null);
  const secretsFetched = useRef(false);

  const effectiveBranch = branch || globalBranch;
  const repoName = trigger.repoName || '';

  useEffect(() => {
    if (!configOpen || subsFetched.current) return;
    subsFetched.current = true;
    setSubsLoading(true);
    setSubsError(null);
    pipelinesApi.getCloudBuildConfig(null, null, trigger.name)
      .then(cfg => setSubsValues(cfg.substitutions || {}))
      .catch(e => setSubsError(e.error || e.message || 'Failed to load config'))
      .finally(() => setSubsLoading(false));
  }, [configOpen]); // eslint-disable-line

  function reloadSubs() {
    subsFetched.current = false;
    setSubsError(null);
    setSubsLoading(true);
    pipelinesApi.getCloudBuildConfig(null, null, trigger.name)
      .then(cfg => { setSubsValues(cfg.substitutions || {}); subsFetched.current = true; })
      .catch(e => setSubsError(e.error || e.message || 'Failed to load config'))
      .finally(() => setSubsLoading(false));
  }

  useEffect(() => {
    if (!configOpen || secretsFetched.current) return;
    secretsFetched.current = true;
    setSecretsLoading(true);
    setSecretsError(null);
    pipelinesApi.listSecrets()
      .then(names => setSecretOptions(names || []))
      .catch(e => setSecretsError(e.error || e.message || 'Failed to load secrets'))
      .finally(() => setSecretsLoading(false));
  }, [configOpen]); // eslint-disable-line

  async function handleSaveSecretConfig() {
    if (!selectedSecret) return;
    setConfigSaving(true);
    setConfigSaveError(null);
    try {
      const updated = await pipelinesApi.setEnvConfig({ triggerName: trigger.name, secretName: selectedSecret });
      setEnvConfigState(updated);
      setEnvResult(null);
    } catch (e) {
      setConfigSaveError(e.error || e.message || 'Failed to save');
    } finally {
      setConfigSaving(false);
    }
  }

  async function handleMarkDisabled() {
    setConfigSaving(true);
    setConfigSaveError(null);
    try {
      const updated = await pipelinesApi.setEnvConfig({ triggerName: trigger.name, disabled: true });
      setEnvConfigState(updated);
      setEnvResult(null);
    } catch (e) {
      setConfigSaveError(e.error || e.message || 'Failed to save');
    } finally {
      setConfigSaving(false);
    }
  }

  function handleEnvCheck() {
    if (!repoName || !effectiveBranch) return;
    setEnvChecking(true);
    setEnvResult(null);
    setEnvError(null);
    pipelinesApi.envCheck({ repo: repoName, branch: effectiveBranch, triggerName: trigger.name })
      .then(r => {
        setEnvResult(r);
        if (r.missingKeys?.length > 0) setEnvKeysModalOpen(true);
      })
      .catch(e => setEnvError(e.error || e.message || 'Env check failed'))
      .finally(() => setEnvChecking(false));
  }

  function handleBuild() {
    if (!effectiveBranch) return;
    setBuilding(true);
    setResult(null);
    setBuildError(null);
    pipelinesApi.triggerDirect({ triggerName: trigger.name, branch: effectiveBranch, substitutions: subsValues })
      .then(r => setResult(r))
      .catch(e => setBuildError(e.error || e.message || 'Build failed'))
      .finally(() => setBuilding(false));
  }

  return (
    <div className="border-b border-slate-100 last:border-0">
      {/* Main row */}
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-slate-800">{trigger.name}</span>
          <EnvConfigBadge status={envConfig.status} />
          {trigger.description && (
            <span className="ml-2 text-[10px] text-slate-400">{trigger.description}</span>
          )}
          {trigger.disabled && (
            <span className="ml-2 text-[10px] font-medium text-amber-500 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5">
              disabled
            </span>
          )}
          {result && (
            <span className="ml-2 text-[10px] font-mono text-purple-400">
              Build triggered → {result.buildId?.slice(0, 8)}…
            </span>
          )}
          {buildError && <span className="ml-2 text-[10px] text-red-400">{buildError}</span>}
        </div>
        <button
          onClick={() => setConfigOpen(true)}
          className="flex items-center gap-1 py-1 px-2.5 text-xs font-medium rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors shrink-0"
        >
          <Settings size={11} />
          Configure
        </button>
      </div>

      {/* Configure drawer (GCP "Run trigger" panel style) */}
      {configOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/20" onClick={() => setConfigOpen(false)} />

          <div className="relative w-full max-w-2xl h-full bg-white shadow-2xl flex flex-col animate-slide-in-right">

            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-200 shrink-0">
              <button
                onClick={() => setConfigOpen(false)}
                className="w-7 h-7 rounded-md hover:bg-slate-100 flex items-center justify-center text-blue-600 transition-colors"
              >
                <X size={16} />
              </button>
              <h3 className="text-base font-medium text-slate-900">Configure trigger</h3>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

              <div>
                <p className="text-sm font-medium text-slate-900 mb-1">Trigger</p>
                <p className="text-sm text-slate-600">{trigger.name}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-900 mb-1">Repository</p>
                <p className="text-sm font-mono text-slate-600">{repoName || '—'}</p>
              </div>

              {/* Branch + Env check */}
              <div>
                <p className="text-sm font-medium text-slate-900 mb-1.5">Branch</p>
                <input
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 transition-colors"
                  placeholder="Branch name…"
                  value={branch}
                  onChange={e => setBranch(e.target.value)}
                />
                <button
                  onClick={handleEnvCheck}
                  disabled={envChecking || !effectiveBranch || !repoName || envConfig.status === 'disabled'}
                  title={
                    envConfig.status === 'disabled' ? 'Env checkup is marked not applicable for this trigger'
                    : !effectiveBranch ? 'Enter a branch first'
                    : 'Check env vars against Secret Manager'
                  }
                  className="mt-2 flex items-center gap-1.5 py-1.5 px-3 text-xs rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-40 transition-colors"
                >
                  {envChecking ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
                  Env Check
                </button>

                {/* Env check result */}
                {envError && (
                  <div className="mt-2 flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    <AlertCircle size={12} className="text-red-400 shrink-0 mt-0.5" />
                    <span className="text-xs text-red-500">{envError}</span>
                  </div>
                )}
                {envResult?.status === 'unconfigured' && (
                  <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    <ShieldQuestion size={12} className="text-amber-500 shrink-0 mt-0.5" />
                    <span className="text-xs text-amber-700">{envResult.message}</span>
                  </div>
                )}
                {envResult?.missingKeys?.length > 0 && (
                  <div className="mt-2 flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    <span className="flex items-center gap-1.5 text-xs text-amber-700">
                      <AlertTriangle size={12} className="shrink-0" />
                      {envResult.missingKeys.length} key{envResult.missingKeys.length !== 1 ? 's' : ''} missing in Secret Manager
                    </span>
                    <button
                      onClick={() => setEnvKeysModalOpen(true)}
                      className="text-[10px] font-medium text-amber-700 hover:text-amber-900 underline shrink-0"
                    >
                      Set values
                    </button>
                  </div>
                )}
                {envResult?.status === 'configured' && envResult.missingKeys?.length === 0 && (
                  <div className="mt-2 flex items-center gap-2 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                    <CheckCircle2 size={12} className="text-green-500" />
                    <span className="text-xs text-green-700">All env vars are present in Secret Manager.</span>
                  </div>
                )}
              </div>

              {/* Env secret configuration */}
              <div>
                <p className="text-sm font-medium text-slate-900 mb-1">Env Checkup Secret</p>
                <p className="text-xs text-slate-500 mb-2">
                  {envConfig.status === 'configured' && (
                    <>Diffing against <span className="font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5">{envConfig.secretName}</span></>
                  )}
                  {envConfig.status === 'disabled' && 'Marked as not applicable — this trigger does not use Secret Manager.'}
                  {envConfig.status === 'unconfigured' && 'No secret chosen yet. Pick one below or mark as not applicable.'}
                </p>

                <div className="flex items-center gap-2">
                  <SearchableSelect
                    options={secretOptions}
                    value={selectedSecret}
                    onChange={setSelectedSecret}
                    placeholder="Select a secret…"
                    loading={secretsLoading}
                    loadingLabel="Loading secrets…"
                  />
                  <button
                    onClick={handleSaveSecretConfig}
                    disabled={!selectedSecret || configSaving}
                    className="flex items-center gap-1.5 py-1.5 px-3 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 transition-colors shrink-0"
                  >
                    {configSaving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                    Save
                  </button>
                  <button
                    onClick={handleMarkDisabled}
                    disabled={configSaving || envConfig.status === 'disabled'}
                    title="This trigger doesn't use Secret Manager"
                    className="flex items-center gap-1.5 py-1.5 px-3 text-xs rounded-md border border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 transition-colors shrink-0"
                  >
                    <ShieldOff size={11} />
                    Skip Env Diff
                  </button>
                </div>

                {secretsError && (
                  <div className="mt-2 flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    <AlertCircle size={12} className="text-red-400 shrink-0 mt-0.5" />
                    <span className="text-xs text-red-400">{secretsError}</span>
                  </div>
                )}
                {configSaveError && (
                  <div className="mt-2 flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    <AlertCircle size={12} className="text-red-400 shrink-0 mt-0.5" />
                    <span className="text-xs text-red-400">{configSaveError}</span>
                  </div>
                )}
              </div>

              {/* Substitutions */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-slate-900">
                    Substitution variables
                    {subsLoading && <span className="ml-2 text-xs font-normal text-slate-400">Loading…</span>}
                  </p>
                  <button
                    onClick={reloadSubs}
                    disabled={subsLoading}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-40 transition-colors"
                  >
                    <RefreshCw size={10} className={subsLoading ? 'animate-spin' : ''} /> Reload
                  </button>
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Substitutions allow re-use of a cloudbuild.yaml file with different variable values.
                </p>

                {subsError && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    <AlertCircle size={12} className="text-red-400 shrink-0 mt-0.5" />
                    <span className="text-xs text-red-400">{subsError}</span>
                  </div>
                )}

                {Object.keys(subsValues).length > 0 && (
                  <div className="space-y-3">
                    {Object.entries(subsValues).map(([key, val]) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-blue-700 mb-0.5">{key}</label>
                        <input
                          className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-mono text-slate-900 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 transition-colors"
                          value={val}
                          placeholder="(empty)"
                          onChange={e => setSubsValues(s => ({ ...s, [key]: e.target.value }))}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {!subsLoading && !subsError && Object.keys(subsValues).length === 0 && (
                  <p className="text-xs text-slate-500">No substitutions found in trigger config.</p>
                )}
              </div>

              {/* Trigger metadata */}
              <div>
                <p className="text-sm font-medium text-slate-900 mb-1.5">Trigger Info</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <span className="text-slate-400">Config file</span>
                  <span className="font-mono text-slate-700">{trigger.filename || 'cloudbuild.yaml'}</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="shrink-0 border-t border-slate-200 px-6 py-4 space-y-3">
              {(result || buildError) && (
                <div>
                  {result && (
                    <span className="text-xs text-green-700">Build triggered → {result.buildId?.slice(0, 8)}…</span>
                  )}
                  {buildError && <span className="text-xs text-red-500">{buildError}</span>}
                </div>
              )}
              <div className="flex items-center gap-4">
                <button
                  onClick={handleBuild}
                  disabled={building || !effectiveBranch || trigger.disabled}
                  className="flex items-center gap-2 py-2 px-4 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {building ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                  Build
                </button>
                <button
                  onClick={() => setConfigOpen(false)}
                  className="text-sm font-medium text-slate-700 hover:text-slate-900"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {envKeysModalOpen && (
        <EnvCheckModal
          missingKeys={envResult?.missingKeys || []}
          repo={repoName}
          triggerName={trigger.name}
          onConfirm={(values, savedKeys) => {
            setEnvKeysModalOpen(false);
            // Only drop the keys that were actually saved to Secret Manager —
            // ones left blank are still missing, so the "all env vars are
            // present" message shouldn't claim otherwise.
            setEnvResult(r => (r ? { ...r, missingKeys: r.missingKeys.filter(k => !savedKeys.includes(k)) } : r));
          }}
          onCancel={() => setEnvKeysModalOpen(false)}
        />
      )}
    </div>
  );
}

// ── Monorepo card (repo with multiple components) ─────────────────────────────
function MonorepoCard({ repo, branch, allRepos = [], triggers = [], triggersLoading = false, triggersError = null }) {
  const [expanded, setExpanded] = useState(false);

  const [selectedDeps, setSelectedDeps] = useState([]);
  const [depsLoading, setDepsLoading] = useState(false);
  const [depsSaving, setDepsSaving] = useState(false);
  const depsFetched = useRef(false);

  const otherRepos = allRepos.filter(r => r.id !== repo.id);

  useEffect(() => {
    if (!expanded || depsFetched.current) return;
    depsFetched.current = true;
    setDepsLoading(true);
    reposApi.getDependencies(repo.id)
      .then(deps => setSelectedDeps(deps.map(d => d.id)))
      .catch(() => {})
      .finally(() => setDepsLoading(false));
  }, [expanded]); // eslint-disable-line

  async function handleSaveDeps() {
    setDepsSaving(true);
    try {
      await reposApi.setDependencies(repo.id, selectedDeps);
    } catch {
      // silently ignore
    } finally {
      setDepsSaving(false);
    }
  }

  return (
    <div className="card transition-all hover:border-slate-300">
      <div
        className="flex items-center gap-3 p-4 cursor-pointer select-none"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
          <Layers size={16} className="text-blue-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">{repo.name}</span>
            {!expanded && repo.components.length > 0 && (
              <span className="text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100 rounded-full px-2 py-0.5">
                {repo.components.length} components
              </span>
            )}
            {expanded && triggers.length > 0 && (
              <span className="text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100 rounded-full px-2 py-0.5">
                {triggers.length} triggers
              </span>
            )}
          </div>
          <span className="text-xs font-mono text-slate-400">{repo.repoUrl}</span>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform shrink-0 ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {expanded && (
        <div className="border-t border-slate-100 max-h-[420px] overflow-y-auto">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
            <p className="text-xs font-semibold text-slate-700 mb-0.5">Depends On</p>
            {/* <p className="text-[10px] text-slate-500 mb-2">These repos must finish deploying before this one during Deploy All.</p> */}
            {/* {depsLoading ? (
              <span className="text-xs text-slate-400">Loading…</span>
            ) : otherRepos.length === 0 ? (
              <p className="text-xs text-slate-500">No other repos available.</p>
            ) : (
              <div className="space-y-1.5">
                {otherRepos.map(r => (
                  <label key={r.id} className="flex items-center gap-2.5 p-2 rounded-lg bg-white border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      className="accent-accent-purple"
                      checked={selectedDeps.includes(r.id)}
                      onChange={() => setSelectedDeps(prev =>
                        prev.includes(r.id) ? prev.filter(x => x !== r.id) : [...prev, r.id]
                      )}
                    />
                    <span className="text-xs text-slate-800">{r.name}</span>
                  </label>
                ))}
              </div>
            )} */}
          </div>

          {triggersLoading && (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-slate-400">
              <Loader2 size={12} className="animate-spin" /> Loading triggers from Cloud Build…
            </div>
          )}
          {triggersError && (
            <div className="flex items-start gap-2 px-4 py-3">
              <AlertCircle size={12} className="text-red-400 shrink-0 mt-0.5" />
              <span className="text-xs text-red-400">{triggersError}</span>
            </div>
          )}
          {!triggersLoading && !triggersError && triggers.length === 0 && (
            <p className="px-4 py-3 text-xs text-slate-400">No Cloud Build triggers found for this repo.</p>
          )}
          {triggers.map((trigger) => (
            <GcpTriggerRow key={trigger.id} trigger={trigger} globalBranch={branch} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function Triggers() {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branch, setBranch] = useState('');
  const [triggersMap, setTriggersMap] = useState({});
  const [triggersLoading, setTriggersLoading] = useState(true);
  const [triggersError, setTriggersError] = useState(null);

  // Load repos and all GCP triggers in parallel on mount
  useEffect(() => {
    const reposPromise = reposApi.list()
      .then(repos => setPipelines(repos.map(r => ({
        id: r.id,
        name: r.name,
        repo: r.name,
        repoUrl: r.repoUrl,
        branch: r.branch || '',
        status: 'idle',
        lastRunAt: null,
        lastStatus: null,
        components: r.components || [],
      }))))
      .catch(() => setPipelines([]));

    const triggersPromise = reposApi.listGcpTriggers()
      .then(triggers => {
        const map = {};
        triggers.forEach(t => {
          if (!t.repoName) return;
          if (!map[t.repoName]) map[t.repoName] = [];
          map[t.repoName].push(t);
        });
        setTriggersMap(map);
      })
      .catch(e => setTriggersError(e.error || e.message || 'Failed to load triggers'))
      .finally(() => setTriggersLoading(false));

    Promise.all([reposPromise, triggersPromise]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="text-accent-purple animate-spin" />
      </div>
    );
  }

  const canDeploy = !!branch;

  return (
    <div className="p-6 space-y-5 animate-fade-in">
      {/* ── Top bar ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Deploy</h2>
          <p className="text-sm text-slate-500">
            {pipelines.length} repo{pipelines.length !== 1 ? 's' : ''} configured
          </p>
        </div>
        <Link to="/triggers/new" className="btn-ghost py-1.5 px-3 text-xs shrink-0">
          <Plus size={13} /> Add Repo
        </Link>
      </div>

      {/* ── Branch bar ── */}
      <div className="card p-4 flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs text-slate-400 mb-1 block">Branch to deploy</label>
          <input
            className="input"
            placeholder="e.g. feature/my-feature or main"
            value={branch}
            onChange={e => setBranch(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Configure — opens the dependency DAG / workflow in the same window */}
          <button
            onClick={() => navigate('/workflow')}
            className="flex items-center gap-2 py-2 px-4 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-colors"
          >
            <Settings size={14} />
            Configure
          </button>

          {/* Deploy All — runs on the Configure/Workflow page, with live status on each node */}
          <button
            onClick={() => navigate('/workflow', { state: { branch, autoDeploy: true } })}
            disabled={!canDeploy}
            title={!canDeploy ? 'Enter a branch to deploy first' : 'Deploy all repos'}
            className="btn-primary py-2 px-4 disabled:opacity-40"
          >
            <GitMerge size={14} /> Deploy All
          </button>
        </div>
      </div>

      {/* ── Repo cards ── */}
      <div className="space-y-3">
        {pipelines.map(pipeline =>
          <MonorepoCard
            key={pipeline.id}
            repo={pipeline}
            branch={branch}
            allRepos={pipelines}
            triggers={triggersMap[pipeline.name] || []}
            triggersLoading={triggersLoading}
            triggersError={triggersError}
          />
        )}

        {pipelines.length === 0 && (
          <div className="card p-12 flex flex-col items-center gap-3 text-center">
            <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center">
              <GitBranch size={22} className="text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-900">No repos configured yet</p>
            <p className="text-xs text-slate-500">Add a repo to start deploying</p>
            <Link to="/triggers/new" className="btn-primary mt-1">
              <Plus size={14} /> Add Repo
            </Link>
          </div>
        )}
      </div>

    </div>
  );
}
